import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Users, Plus, Trash2, Crown, AlertTriangle, Mail, UserPlus, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { formatUSPhone } from "../lib/phone";

export default function TeamSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/members");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load team");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.full_name || m.email}? They'll lose access immediately.`)) return;
    try {
      await api.delete(`/team/members/${m.id}`);
      toast.success(`${m.full_name || m.email} removed`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Remove failed");
    }
  };

  if (loading) return <div className="bg-white border border-slate-200 rounded-sm p-6 text-sm text-slate-400">Loading team…</div>;
  if (!data) return null;

  const planCap = data.seat_limit;
  const planNote =
    planCap === 0 ? "Your plan doesn't include teammates. Upgrade to Pro ($99/mo) for 3 seats or Enterprise ($199/mo) for 15."
    : `${data.seats_used}/${planCap} seats used · plan: ${data.plan}`;
  const planLocked = planCap === 0;

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="team-section">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center rounded-sm"><Users className="w-4 h-4" /></div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Team</h2>
            {data.is_teammate && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-sm" data-testid="team-teammate-banner">
                You're a teammate — contact the primary investor to add others.
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500">{planNote}</p>
        </div>
        {!data.is_teammate && (
          <button
            onClick={() => setDialogOpen(true)}
            disabled={!data.can_invite}
            data-testid="team-invite-button"
            className="btn-primary-dark text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <UserPlus className="w-4 h-4" /> Invite teammate
          </button>
        )}
      </div>

      {planLocked && !data.is_teammate && (
        <div className="rounded-sm bg-amber-50 border border-amber-200 p-3 text-sm flex items-start gap-2 mb-4" data-testid="team-upgrade-hint">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-amber-900">
            Multi-user accounts let multiple people manage the same portfolio — shared properties, tenants, loans, and vendors. Available on Pro and Enterprise.
          </div>
        </div>
      )}

      <div className="border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr>
              <th className="px-4 py-2 overline text-slate-600">Name</th>
              <th className="px-4 py-2 overline text-slate-600">Email</th>
              <th className="px-4 py-2 overline text-slate-600">Phone</th>
              <th className="px-4 py-2 overline text-slate-600">Role</th>
              <th className="px-4 py-2 overline text-slate-600 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-100 bg-white" data-testid="team-row-primary">
              <td className="px-4 py-3 font-bold text-slate-900">{data.primary_landlord.full_name}</td>
              <td className="px-4 py-3 text-xs font-mono text-slate-700">{data.primary_landlord.email}</td>
              <td className="px-4 py-3 text-xs font-mono text-slate-500">—</td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-violet-100 text-violet-800 px-2 py-0.5 rounded-sm">
                  <Crown className="w-3 h-3" /> Primary
                </span>
              </td>
              <td className="px-4 py-3 text-right text-xs text-slate-400">—</td>
            </tr>
            {data.members.length === 0 && (
              <tr><td colSpan={5} className="text-center text-sm text-slate-400 py-8">
                {planLocked ? "Upgrade to invite teammates." : "No teammates yet. Click 'Invite teammate' to add one."}
              </td></tr>
            )}
            {data.members.map((m) => (
              <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`team-row-${m.id}`}>
                <td className="px-4 py-3 font-bold text-slate-900">{m.full_name}</td>
                <td className="px-4 py-3 text-xs font-mono text-slate-700">{m.email}</td>
                <td className="px-4 py-3 text-xs font-mono text-slate-500">{m.phone ? formatUSPhone(m.phone) : "—"}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm">Teammate</span>
                </td>
                <td className="px-4 py-3 text-right">
                  {!data.is_teammate && (
                    <button onClick={() => remove(m)} className="text-slate-500 hover:text-red-600 p-1" data-testid={`team-remove-${m.id}`} title="Remove"><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InviteDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onInvited={() => { setDialogOpen(false); load(); }} />
    </section>
  );
}

function InviteDialog({ open, onClose, onInvited }) {
  const [form, setForm] = useState({ email: "", full_name: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm({ email: "", full_name: "", password: "", phone: "" }); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        password: form.password,
      };
      if (form.phone.trim()) payload.phone = form.phone;
      await api.post("/team/members", payload);
      toast.success(`${form.full_name} invited. They can log in with ${form.email}.`);
      onInvited();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="team-invite-dialog">
        <DialogHeader><DialogTitle style={{fontFamily:"Outfit"}}>Invite a teammate</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-slate-500">They'll share full access to your properties, tenants, vendors, and loans. Their login is separate so you can revoke it at any time.</p>
          <Field label="Full name"><input required className="input-clean w-full" data-testid="team-invite-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Field>
          <Field label="Work email"><input required type="email" className="input-clean w-full" data-testid="team-invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Phone (optional)"><input type="tel" inputMode="tel" className="input-clean w-full" data-testid="team-invite-phone" placeholder="(555) 123-4567" value={formatUSPhone(form.phone)} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 11) })} /></Field>
          <Field label="Temporary password (min 6 chars)"><input required minLength={6} className="input-clean w-full" data-testid="team-invite-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
          <DialogFooter>
            <button type="button" onClick={onClose} className="btn-outline-dark">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary-dark flex items-center gap-2" data-testid="team-invite-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send invite
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="overline text-slate-600 mb-1">{label}</div>{children}</label>;
}
